import type { GasType, OwnerEntity } from '@bhops/shared';
import { apiClient } from './client';

// Plan §E-3 — typed client for the field team's grill-order
// workflow.

export interface InfrastructureOrderDto {
  id: string;
  contractId: string;
  customerId: string;
  storeId: string | null;
  ownerEntity: OwnerEntity;
  gasType: GasType | null;
  /**
   * 2026-06-12 — upsert 응답에만 동봉되는 설치 완료 캐스케이드 요약
   * (installCompletedAt set 저장 시). 예약 로봇 0대(조용한 no-op)를 모달이
   * 경고로 가시화하는 데 사용. list/get 응답에는 없음.
   */
  cascade?: {
    deployedCount: number;
    totalReserved: number;
    handoffUpdated: boolean;
  } | null;
  // round3 #1 — 사업팀이 고객사 생성 모달에서 입력한 자유텍스트 가스 타입.
  // gasType(enum)이 null 일 때(LPG/LNG/미정 외: 자체/전기그리들/도시가스) 표시 fallback.
  grillSelectorType: string | null;
  contactedAt: string | null;
  contactNote: string | null;
  grillOrderedAt: string | null;
  grillDeliveredAt: string | null;
  installScheduledAt: string | null;
  /** 2026-10-19 — 각 날짜의 시각 'HH:mm'(없으면 하루 종일). 옛 서버는 안 준다. */
  grillOrderedTime?: string | null;
  grillDeliveredTime?: string | null;
  installScheduledTime?: string | null;
  installCompletedAt: string | null;
  notes: string | null;
  // S1 Phase 11 (§0.8) — 계약 완료 시 Customer 에서 InfraOrder 로 snapshot 되는
  // prefill 필드. 발주 시점 값으로 고정되어 이후 customer 편집과 무관하게 보존.
  // 필드팀 인프라 발주 roster(S3 §3.2)가 표시 — 우선순위는 snapshot > 라이브
  // customer 관계. 백엔드 list() 가 include(select 미사용)라 scalar 컬럼은
  // 모두 반환되므로 별도 DTO 확장만으로 충분 (list select 변경 불필요).
  customerMapUrl: string | null;
  customerContactName: string | null;
  customerContactPosition: string | null;
  customerContactPhone: string | null;
  createdAt: string;
  updatedAt: string;
  // data-lifecycle §3 (PR-5c) — soft-delete 스탬프(삭제됨 배지 + 복구용). list include
  // 라 자동 wire; includeDeleted 목록(필드 쓰기권한)에서만 non-null.
  deletedAt?: string | null;
  // Includes (server returns these on every read):
  contract?: {
    id: string;
    humanId: string;
    status: string;
    startDate?: string;
    endDate?: string;
    ownerEntity?: OwnerEntity;
    // round5 #22 — 사업팀 입력 '희망 도입일'(Deal.expectedCloseAt). 인프라
    // 모달의 설치 예정일 아래 참고 표기용.
    deal?: { expectedCloseAt: string | null } | null;
  };
  // 수정 사항2.pdf p.3 #2 — 인프라 발주 목록에서 고객 담당자/매장 주소를
  // 한 행에서 바로 확인할 수 있도록 backend include 가 4 필드 추가됨.
  customer?: {
    id: string;
    displayName: string;
    legalName?: string;
    // windows-ux-edit Round 2 Phase D (#6) — 위치 URL live fallback 용.
    addressMapUrl?: string | null;
    primaryContactName?: string | null;
    primaryContactPosition?: string | null;
    primaryContactPhone?: string | null;
  };
  store?: {
    id: string;
    name: string;
    branchName: string | null;
    address?: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
  } | null;
}

export interface UpsertInfrastructureOrderBody {
  contractId: string;
  gasType?: GasType;
  contactedAt?: string;
  contactNote?: string;
  grillOrderedAt?: string;
  grillDeliveredAt?: string;
  installScheduledAt?: string;
  installCompletedAt?: string;
  notes?: string;
  /** round5 #9 — 서명 없는 설치 완료 사유(AuditLog 기록 전용, 선택). */
  completionReason?: string;
}

export interface ListInfrastructureOrdersQuery {
  installFrom?: string;
  installTo?: string;
  gasType?: GasType;
  pendingOnly?: boolean;
  // docs/work_log/pdf-feedback-followups-2026-05-24 — StoreDetailPage
  // 의 InfrastructureSection 사용. customer 단위 모든 인프라 발주.
  customerId?: string;
  // data-lifecycle §3 (PR-5c) — 삭제됨 포함(필드 쓰기권한 전용; 서버에서 확인).
  includeDeleted?: boolean;
}

export const infrastructureOrdersApi = {
  async list(query: ListInfrastructureOrdersQuery = {}): Promise<InfrastructureOrderDto[]> {
    const { data } = await apiClient.get<InfrastructureOrderDto[]>('/infrastructure-orders', {
      params: query,
    });
    return data;
  },
  // data-lifecycle §3/D3 (PR-5c) — 인프라 발주 소프트삭제(필드팀 셀프, 복구 가능).
  // round7 의 ADMIN hard-only → soft-delete 로 전환.
  async remove(id: string): Promise<{ id: string; deletedAt: string | null }> {
    const { data } = await apiClient.delete<{ id: string; deletedAt: string | null }>(
      `/infrastructure-orders/${encodeURIComponent(id)}`,
    );
    return data;
  },
  // soft-delete 복구(셀프).
  async restore(id: string): Promise<{ id: string; deletedAt: string | null }> {
    const { data } = await apiClient.post<{ id: string; deletedAt: string | null }>(
      `/infrastructure-orders/${encodeURIComponent(id)}/restore`,
    );
    return data;
  },
  // 셀프 완전삭제(소프트삭제된 발주만).
  async hardDelete(id: string): Promise<{ id: string; deleted: boolean }> {
    const { data } = await apiClient.delete<{ id: string; deleted: boolean }>(
      `/infrastructure-orders/${encodeURIComponent(id)}/permanent`,
    );
    return data;
  },
  async byContract(contractId: string): Promise<InfrastructureOrderDto | null> {
    const { data } = await apiClient.get<InfrastructureOrderDto | null>(
      `/infrastructure-orders/by-contract/${contractId}`,
    );
    return data;
  },
  async upsert(body: UpsertInfrastructureOrderBody): Promise<InfrastructureOrderDto> {
    const { data } = await apiClient.post<InfrastructureOrderDto>('/infrastructure-orders', body);
    return data;
  },
  // S1 Phase 7 (R2 #6) — '연락 요청' 버튼. 필드팀에 INFRA_CONTACT_REQUESTED
  // 알람 발송.
  async requestContact(id: string): Promise<{ status: string }> {
    const { data } = await apiClient.post<{ status: string }>(
      `/infrastructure-orders/${id}/request-contact`,
    );
    return data;
  },
};
